import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import ListPlaceholder from "../components/ListPlaceholder";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "../ui-components/Notifications";
import prepareConfigurationStep from "../utils/prepareConfigurationStep";
import convertSurveyResponses from "../utils/convertSurveyResponses";
import ConfiguratorSurvey from "../components/ConfiguratorSurvey";
import checkIfStepIsVisible from "../utils/checkIfStepIsVisible";
import useGetServiceConfiguration from "../hooks/useGetServiceConfiguration";
import ConfiguratorContext from "../components/ConfiguratorContext";
import CountryConfigContext from "../CountryConfigContext";
import useFetchFixedProducts, {
  fetchProductsById,
  getConfiguratorProductsToFetch,
  reduceProducts,
} from "../ui-components/pages/Users/hooks/useFetchFixedProducts";
import useFetchFixedSystems, {
  fetchSystemsById,
  getConfiguratorSystemsToFetch,
  reduceSystems,
} from "../ui-components/pages/Users/hooks/useFetchFixedSystems";
import FixedLoader from "../components/FixedLoader";
import getAllConfiguratorOutputs from "../utils/getAllConfiguratorOutputs";
import UserNotificationsContext from "../UserNotificationsContext";
import getKccResultContextKey from "../utils/getKccResultContextKey";
import {isAssociatedServiceVisibile} from "@feature/surveys/lib/design-quotation/permissions";
import {getRemoteSpreadsheetResults, getRemoteKColorResults} from "@feature/remote-calculation-results";
import {
  useCrossServicesConfigurations
} from "@feature/cross_services_configurations/hooks/useCrossServicesConfigurations";
import {prepareCrossData} from "../utils/prepareCrossData";

const PermanentSurvey = ({ returnPath, worksite, userData, updateServicesSurveys }) => {
  const { push: historyPush } = useHistory();
  const { push } = useContext(NotificationsContext);
  const { userNotifications, setUserNotifications } = useContext(UserNotificationsContext);
  const {
    config: { products, systems, services },
  } = useContext(CountryConfigContext);
  const intl = useIntl();
  const [visibleSteps, setVisibleSteps] = useState([]);
  const { surveyId, serviceId } = useParams();
  // eslint-disable-next-line
  const [survey, setSurvey] = useState(false);
  const [sourceSurveys, setSourceSurveys] = useState([]);
  const [service, setService] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const [responses, setResponses] = useState({});
  // used for hubspot ticket 42912186232 in order to remove autogenerate responses of questions
  // inside a recursive question.
  const [autogeneratedResponsesToRemove, setAutogeneratedResponsesToRemove] = useState({});
  const [data, setData] = useState(null);
  const [crossData, setCrossData] = useState(null);

  // kcc states
  const [colors, setColors] = useState(null);
  const [kccOptimizationResults, setKccOptimizationResults] = useState({});
  const [connectionResults, setConnectionResults] = useState({});
  const [spreadsheetResults, setSpreadsheetResults] = useState({});
  const [kColorResults, setKColorResults] = useState({});
  const [isCalculatingSpreadsheetsResults, setIsCalculatingSpreadsheetsResults] = useState(false);
  const [isCalculatingKColorResults, setIsCalculatingKColorResults] = useState(false);
  const [kColorPricesChosen, setKColorPricesChosen] = useState({});
  const [contextCache, setContextCache] = useState();

  const apiRequest = useCountryAxiosInstance();
  const getServiceConfiguration = useGetServiceConfiguration(undefined, {
    survey_id: parseInt(surveyId, 10),
  });
  const {getCrossServicesConfigurations} = useCrossServicesConfigurations();

  const getVisibleSteps = useCallback((context) => {
    return context.data.filter((step) => checkIfStepIsVisible(step, context));
  }, []);

  const saveResponses = useCallback(
    async (questionsIds) => {
      let requests = questionsIds
        .filter((id) => responses[id] && responses[id].toSave)
        .map((id) => {
          const data = {
            service_survey_id: parseInt(surveyId),
            value: JSON.stringify(responses[id].value),
            configuration_input_id: responses[id].question.id,
            r_perm: worksite?.r_perm ?? [`user_${userData.id}`],
            w_perm: worksite?.w_perm ?? [`user_${userData.id}`],
            configuration_option_id: responses[id]?.option?.id || null,
            recursive_question_value:
              responses[id]?.recursive_question_value || [],
            configuration_input_version: responses[id].question.version,
            configuration_option_version: responses[id]?.option?.version || null,
            ...(responses[id].service_block_id ? {
              service_block_id: responses[id].service_block_id,
              service_block_version: responses[id].service_block_version,
            }: {}),
          };

          if (responses[id]?.id) {
            return apiRequest.put(
              `/service_survey_responses/${responses[id].id}`,
              data
            );
          }

          return apiRequest.post(`/service_survey_responses`, data);
        });

      if (requests.length) {
        const firstRender = survey.complete ? false : survey.first_render
        // console.log({...survey, modified_at: new Date(), id_modifier: userData.id, first_render: firstRender})
        const update_survey = apiRequest.put(`/service_surveys/${parseInt(surveyId)}`,
          {...survey, modified_at: new Date(), id_modifier: userData.id, first_render: firstRender})
        requests = [update_survey, ...requests]


        const results = await Promise.all(requests);
        await updateServicesSurveys();
        const context = {
          data,
          configuration,
          products,
          systems,
          survey,
          outputs: getAllConfiguratorOutputs(data),
          responses,
          crossData,
        };
        const newResponses = convertSurveyResponses(results.slice(1), context);

        setResponses(newResponses);
        setVisibleSteps(getVisibleSteps({...context, responses: newResponses}));

        return newResponses;
      }
      return true
    },
    [
      survey,
      responses,
      products,
      systems,
      configuration,
      data,
      apiRequest,
      surveyId,
      getVisibleSteps,
      worksite,
      userData.id,
      updateServicesSurveys,
    ]
  );

  useEffect(() => {
    let cancel = false;
    const fetchData = async () => {
      try {
        const [service, survey] = await Promise.all([
          apiRequest.get(`/services/${serviceId}`),
          apiRequest.get(`/service_surveys/${surveyId}`),
        ]);
        const [
          responses,
          configuration,
          crossServicesConfigurations,
          colors,
          kccOptimizationResults,
          connectionResults,
          spreadsheetResults,
          kColorResults,
        ] = await Promise.all([
          apiRequest.get(
            `/service_survey_responses/all?service_survey_id=${surveyId}`
          ),
          getServiceConfiguration(serviceId, survey.service_version),
          getCrossServicesConfigurations(serviceId, survey.service_version),
          apiRequest.get("/tintometers/colors/all"),
          apiRequest.get(`/tintometers/optimization_result/${surveyId}`, {
            params: { tintometer_flg: service.type === "tintometer" },
          }),
          apiRequest.get(`/connectors/${surveyId}`),
          getRemoteSpreadsheetResults(parseInt(surveyId), apiRequest),
          getRemoteKColorResults(parseInt(surveyId), apiRequest)
        ]);

        const sourceSurveys = survey.id_source_surveys?.length > 0
          ? await apiRequest.get(
              "/service_surveys",
              {params: {ids: survey.id_source_surveys.join(',')}}
            )
          : [];

        const crossConfigurationsData = prepareCrossData(crossServicesConfigurations);
        const data = prepareConfigurationStep(configuration, undefined, service.id);
        // fetch products
        const productsToFetch = getConfiguratorProductsToFetch(data, {
          products,
        });
        let configuratorProducts = products;
        if (productsToFetch.length) {
          const missingProducts = await fetchProductsById(
            apiRequest,
            productsToFetch
          );
          configuratorProducts = {
            ...configuratorProducts,
            ...reduceProducts(missingProducts),
          };
        }

        // fetch systems
        const systemsToFetch = getConfiguratorSystemsToFetch(data, {
          systems,
        });
        let configuratorSystems = systems;
        if (systemsToFetch.length) {
          const missingSystems = await fetchSystemsById(
            apiRequest,
            systemsToFetch
          );
          configuratorSystems = {
            ...configuratorSystems,
            ...reduceSystems(missingSystems),
          };
        }

        const context = {
          data,
          configuration,
          products: configuratorProducts,
          systems: configuratorSystems,
          service,
          survey,
          outputs: getAllConfiguratorOutputs(data),
          kccOptimizationResults,
          connectionResults,
          spreadsheetResults,
          kColorResults,
          kColorPricesChosen,
          crossData: crossConfigurationsData,
        };

        const convertedResponses = convertSurveyResponses(responses, context);

        if (!cancel) {
          setData(data);
          setCrossData(crossConfigurationsData);
          setResponses(convertedResponses);
          setConfiguration(configuration);
          setSurvey(survey);
          setSourceSurveys(sourceSurveys);
          setService(service);
          setVisibleSteps(
            getVisibleSteps({ ...context, responses: convertedResponses })
          );
          setColors(colors);
          setKccOptimizationResults(kccOptimizationResults.reduce(
            (prev, curr) => {
              const kccResultContextKey = getKccResultContextKey({
                color: curr.cod_color,
                system: curr.cod_system,
                surface: curr.val_surface,
                survey: curr.cod_survey,
                primers: curr.lst_primers,
                reti: curr.lst_reti,
                rasanti: curr.lst_rasanti,
                is_tintometer: curr.is_tintometrist_version
              });
              return {...prev, [kccResultContextKey]: curr};
            },
            {}
          ));
          setConnectionResults(connectionResults);
          setSpreadsheetResults(spreadsheetResults);
          setKColorResults(kColorResults);
          setContextCache(context);
        }
      } catch (error) {
        console.log(error);
        push({
          title: intl.formatMessage({ id: "server_error" }),
          type: "error",
        });
        historyPush(returnPath);
      }
    };

    if (!data) {
      fetchData();
    }

    return () => {
      cancel = true;
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Refresh steps visibility when remote calculation results are updated. Other than this, the steps visibility is
    // updated in other two cases: on the initial fetch, and when the responses are saved (user clicks on "proceed"). If
    // the user is in the middle of a step and performs a remote calculation in an output, the steps visibility should
    // be updated as well because the results could affect the visibility of the steps (or their internal blocks)
    if (!contextCache)
      return;
    const contextOverrides = {responses, connectionResults, spreadsheetResults};
    setVisibleSteps(getVisibleSteps({ ...contextCache, ...contextOverrides }));
  }, [connectionResults, spreadsheetResults]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if the survey has never been seen by the current user, delete the 'new survey' notification
    const newSurveyNotification = userNotifications
      ?.find((n) => n.val_type === 'worksite_survey' && n.cod_survey === parseInt(surveyId));
    if (newSurveyNotification)
      setUserNotifications((prev) => prev.filter((n) => n.id !== newSurveyNotification.id));
  }, [userNotifications]);  // eslint-disable-line react-hooks/exhaustive-deps

  // fetch needed products
  const fetchFixedProducts = useFetchFixedProducts(data);
  if (fetchFixedProducts === "error") {
    throw new Error("error on fetch products");
  }

  // fetch needed systems
  const fetchFixedSystems = useFetchFixedSystems(data);
  if (fetchFixedSystems === "error") {
    throw new Error("error on fetch systems");
  }

  if (!fetchFixedSystems || !fetchFixedSystems || !configuration || !data || !responses) {
    return <FixedLoader />;
  }
  
  return (
    <>
      <div className="fixed z-0 top-0 left-0 w-full h-full z-10 bg-black opacity-50"></div>
      <div
        className="fixed z-10 top-0 left-0 bg-white w-full h-screen overflow-y-auto overflow-x-hidden"
        id="survey-portal"
      >
        {!survey ? (
          <div className="container mx-auto px-6 flex flex-col">
            <ListPlaceholder />
          </div>
        ) : (
          <ConfiguratorContext.Provider
            value={{
              configuration,
              data,
              crossData,
              responses,
              setResponses,
              autogeneratedResponsesToRemove,
              setAutogeneratedResponsesToRemove,
              products,
              systems,
              service,
              survey,
              setSurvey,
              sourceSurveys,
              outputs: getAllConfiguratorOutputs(data),
              colors,
              kccOptimizationResults,
              setKccOptimizationResults,
              connectionResults,
              setConnectionResults,
              spreadsheetResults,
              setSpreadsheetResults,
              kColorResults,
              setKColorResults,
              isCalculatingSpreadsheetsResults,
              setIsCalculatingSpreadsheetsResults,
              isCalculatingKColorResults,
              setIsCalculatingKColorResults,
              kColorPricesChosen,
              setKColorPricesChosen,
              context: "WEB",
              worksite_freezed: worksite?.terminated?.terminated_by_creator
            }}
          >
            <ConfiguratorSurvey
              returnPath={returnPath}
              saveResponses={saveResponses}
              visibleSteps={visibleSteps}
              updateServicesSurveys={updateServicesSurveys}
              associatedServices={services
                .filter((s) =>
                  s.class === 1 /* Quotazione */
                  && isAssociatedServiceVisibile(
                    userData,
                    s.associated_services?.[service.id]?.r_perm || [],
                  )
                  && s.active
              )}
            />
          </ConfiguratorContext.Provider>
        )}
      </div>
    </>
  );
};

export default PermanentSurvey;
